import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { SearchCircleIcon, CheckCircleIcon, LocationMarkerIcon, CurrencyDollarIcon } from '@heroicons/react/solid';
import {
  ArrowRightIcon,
  ClipboardListIcon,
  DocumentTextIcon,
  GlobeAltIcon,
  PencilAltIcon,
  UserAddIcon,
  LightningBoltIcon,
  UserGroupIcon,
  BellIcon,
  InboxInIcon,
  SearchIcon,
} from '@heroicons/react/outline';
import { useAuth } from '../contexts/AuthContext';
import MapleLeaf from '../components/MapleLeaf';
import abIcon from '../assets/ab.ico';
import ableIcon from '../assets/able.ico';
import youthIcon from '../assets/youth.ico';
import refugeeIcon from '../assets/refugee.png';

// REV-002: public employer landing page. Signed-in users go straight to the dashboard.
// Positioning (owner, 2026-09-24): a general job board for groups that are often overlooked.
// Never market it around LMIAs or immigration outcomes, since credibility with IRCC/ESDC comes first.

// Accent colours are each board's own brand colour; the page itself stays on the red primary.
const BOARDS = [
  { name: 'NewJobLeads', short: 'New', host: 'www.newjobleads.ca', audience: 'New immigrants and newcomers to Canada', color: '#48bb78', icon: null },
  { name: 'YouthJobLeads', short: 'Youth', host: 'www.youthjobleads.ca', audience: 'Young Canadians starting their careers', color: '#6366f1', icon: youthIcon },
  { name: 'AbleJobLeads', short: 'Able', host: 'www.ablejobleads.ca', audience: 'People with disabilities', color: '#2563eb', icon: ableIcon },
  { name: 'RefugeeJobLeads', short: 'Refugee', host: 'refugeejobleads.ca', audience: 'Refugees building a new life in Canada', color: '#ff8000', icon: refugeeIcon },
  { name: 'AboriginalJobLeads', short: 'Aboriginal', host: 'www.aboriginaljobleads.ca', audience: 'Aboriginal and Indigenous job seekers', color: '#d97706', icon: abIcon },
];

// Keep in sync with PRICING in Postjob.jsx, PricingSummary.jsx and ExtendRenewModal.jsx.
const PRICES = [
  { days: 91, price: 30, label: '3 months' },
  { days: 181, price: 50, label: '6 months' },
  { days: 365, price: 60, label: '12 months', badge: 'Best value' },
];

const STEPS = [
  { icon: UserAddIcon, title: 'Create your account', text: 'Sign up with your work email and add your company profile.' },
  { icon: ClipboardListIcon, title: 'Write your posting', text: 'Describe the role, add the salary and choose which boards it goes to.' },
  { icon: LightningBoltIcon, title: 'Go live', text: 'Right after payment, your job is live on the boards you chose and in our national job search. Edit, extend or renew any time.' },
  { icon: InboxInIcon, title: 'Review applicants', text: 'Candidates apply on the board with a cover letter and résumé. Each application lands in your dashboard, with a copy in your inbox.' },
];

// "Built for simpler recruitment" (owner's marketing theme, 2026-09-28): publish once, distribute
// across specialized boards, applications in one place, email notifications, recruitment history.
const FEATURES = [
  { icon: GlobeAltIcon, title: 'Publish once', text: 'Write your posting once and choose any combination of our five specialized boards. Every posting also appears in our national job search. No need to post five times.' },
  { icon: UserGroupIcon, title: 'Applications in one place', text: 'See every applicant, cover letter and résumé in your recruiter dashboard. Filter by board, update each applicant’s status, and export to CSV.' },
  { icon: InboxInIcon, title: 'Email notifications', text: 'Get an email as soon as someone applies, with their résumé attached. Reply straight to the candidate from your inbox.' },
  { icon: DocumentTextIcon, title: 'Detailed recruitment history', text: 'Every posting keeps its exact posted and expiry dates, and a printable record shows where and when it ran and how many applications it received.' },
  { icon: BellIcon, title: 'Reminders before expiry', text: 'We email you a week before a posting expires, with a one-click link to extend it. Renew an expired job any time.' },
  { icon: PencilAltIcon, title: 'Edit any time', text: 'Update the description, salary or contact details while your job is live.' },
];

const FAQS = [
  {
    q: 'How long does a posting stay live?',
    a: 'You choose 91, 181 or 365 days when you post. You can extend a live posting or renew an expired one from your dashboard.',
  },
  {
    q: 'Where will my job appear?',
    a: 'On each community board you choose, and in our national job search on CanadaJobLeads.ca, where job seekers across Canada can find it. Both are included in the price of your posting.',
  },
  {
    q: 'How do candidates apply?',
    a: 'Right on the job page: a short form with their contact details, an optional cover letter and their résumé. You see every application in your dashboard, and a copy goes to the email address on your posting.',
  },
  {
    q: 'What happens to applicant information?',
    a: 'Only you can see the applications for your jobs. We keep them for 6 months after a posting closes, email you two weeks before they are deleted, and then remove them to protect applicants’ privacy.',
  },
  {
    q: 'How do I pay?',
    a: 'By card at checkout, in Canadian dollars, processed securely by Stripe. Any account balance is applied automatically first.',
  },
];

const CONTACT_EMAIL = 'enquiries@canadajobleads.ca';

// Shows the address as text with a Copy button, because a mailto link does nothing
// when no mail app is set up.
function CopyEmail({ className = '', buttonClassName = '' }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard
      .writeText(CONTACT_EMAIL)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => console.log(err));
  }
  return (
    <span className={`inline-flex flex-wrap items-center gap-2 ${className}`}>
      <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold hover:underline">
        {CONTACT_EMAIL}
      </a>
      <button type="button" onClick={copy} className={buttonClassName}>
        {copied ? 'Copied!' : 'Copy'}
      </button>
    </span>
  );
}

// Illustrative product preview for the hero, clearly labelled as an example.
function ExamplePosting() {
  return (
    <div className="relative mx-auto w-full max-w-md">
      <div className="absolute -inset-4 rounded-3xl bg-gradient-to-tr from-primary/20 to-rose-200/40 blur-2xl" aria-hidden="true" />
      <div className="relative rounded-2xl border border-gray-100 bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between">
          <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-500">Example posting</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-semibold text-green-700">
            <span className="h-1.5 w-1.5 rounded-full bg-green-500" /> Live
          </span>
        </div>
        <h3 className="mt-4 text-xl font-bold text-gray-900">Line Cook</h3>
        <p className="text-sm text-gray-500">Your Company Ltd.</p>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700">
          <span className="inline-flex items-center"><LocationMarkerIcon className="mr-1 h-4 w-4 text-primary" />Vancouver, BC</span>
          <span className="inline-flex items-center"><CurrencyDollarIcon className="mr-1 h-4 w-4 text-primary" />$19.50/hour</span>
        </div>
        <p className="mt-2 text-xs text-gray-500">Posted on 2026-09-16 · Expires 2026-12-16</p>
        <div className="mt-5 border-t border-gray-100 pt-4">
          <p className="text-xs font-medium text-gray-500">Live on</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {BOARDS.map((b) => (
              <span
                key={b.name}
                className="rounded-md px-2 py-1 text-xs font-semibold"
                style={{ color: b.color, backgroundColor: `${b.color}14`, border: `1px solid ${b.color}40` }}
              >
                {b.short}
              </span>
            ))}
            <span className="rounded-md border border-gray-300 bg-gray-900 px-2 py-1 text-xs font-semibold text-white">National search</span>
          </div>
        </div>
      </div>
      <div className="absolute -bottom-5 -left-4 hidden sm:flex items-center gap-2 rounded-xl border border-gray-100 bg-white px-3 py-2 shadow-lg">
        <DocumentTextIcon className="h-5 w-5 text-primary" />
        <span className="text-xs font-semibold text-gray-800">Posting record ready</span>
      </div>
    </div>
  );
}

function SectionHeading({ eyebrow, title, text }) {
  return (
    <div className="max-w-2xl">
      {eyebrow && <p className="text-sm font-semibold uppercase tracking-wider text-primary">{eyebrow}</p>}
      <h2 className="mt-2 text-3xl md:text-4xl font-extrabold font-[Poppins] text-gray-900">{title}</h2>
      {text && <p className="mt-3 text-lg text-gray-600">{text}</p>}
    </div>
  );
}

// GROWTH-004: the job seekers' way in, on the employer landing page. Goes to /jobs?q=…
function JobSearchCard() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  function submit(e) {
    e.preventDefault();
    navigate(q.trim() ? `/jobs?q=${encodeURIComponent(q.trim())}` : '/jobs');
  }
  return (
    <form onSubmit={submit} role="search" className="mt-8 max-w-xl rounded-2xl bg-gray-900 p-4 shadow-xl sm:p-5">
      <label htmlFor="landing-job-search" className="flex items-center gap-2 text-base font-semibold text-white">
        <SearchIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
        Looking for work? Search jobs across Canada
      </label>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row">
        <input
          id="landing-job-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          maxLength={100}
          placeholder="Job title, keyword or company"
          className="w-full rounded-xl border-0 px-4 py-3 text-gray-900 placeholder-gray-400 transition focus:outline-none focus:ring-4 focus:ring-white/25"
        />
        <button type="submit" className="cursor-pointer whitespace-nowrap rounded-xl bg-primary px-5 py-3 font-semibold text-white hover:opacity-90">
          Search jobs
        </button>
      </div>
    </form>
  );
}

function Home() {
  const { currentUser } = useAuth();
  if (currentUser) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen flex flex-col bg-white text-gray-900">
      {/* Navigation */}
      <header className="sticky top-0 z-20 border-b border-gray-100 bg-white/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center whitespace-nowrap">
            <SearchCircleIcon className="text-primary sm:mr-2 w-9 h-9" aria-hidden="true" />
            <span className="hidden sm:inline font-extrabold font-[Poppins] text-xl text-primary">Canada Job Leads</span>
            <span className="sr-only sm:hidden">Canada Job Leads</span>
          </Link>
          <nav className="ml-2 flex items-center gap-2 sm:gap-6">
            <a href="#boards" className="hidden md:inline text-sm font-medium text-gray-600 hover:text-primary">Our boards</a>
            <a href="#how" className="hidden md:inline text-sm font-medium text-gray-600 hover:text-primary">How it works</a>
            <a href="#pricing" className="hidden md:inline text-sm font-medium text-gray-600 hover:text-primary">Pricing</a>
            <Link
              to="/jobs"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-gray-900 px-3 sm:px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-900 hover:text-white"
            >
              <SearchIcon className="h-4 w-4" aria-hidden="true" />
              Search jobs
            </Link>
            <Link to="/sign-in" className="hidden sm:inline whitespace-nowrap text-sm font-semibold text-gray-800 hover:text-primary">Sign in</Link>
            <Link to="/sign-up" className="whitespace-nowrap rounded-full bg-primary px-3 sm:px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90">
              Post a job
            </Link>
          </nav>
        </div>
      </header>

      <main className="flex-grow">
        {/* Hero */}
        <section className="relative overflow-hidden bg-gradient-to-b from-red-50 via-white to-white">
          <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
          <div className="pointer-events-none absolute top-40 -left-32 h-80 w-80 rounded-full bg-rose-200/40 blur-3xl" aria-hidden="true" />
          <MapleLeaf className="pointer-events-none absolute -right-16 bottom-0 h-[420px] w-[420px] text-primary/5" />

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-24 grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-red-100 bg-white px-3 py-1 text-xs font-semibold text-primary shadow-sm">
                <MapleLeaf className="h-3.5 w-3.5" /> Canadian job boards for inclusive hiring
              </span>
              <h1 className="mt-5 text-4xl md:text-5xl font-extrabold font-[Poppins] leading-tight tracking-tight">
                One job posting.
                <span className="block text-primary">Five specialized Canadian audiences.</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg text-gray-600">
                Reach newcomers, youth, Indigenous candidates, people with disabilities, refugees and the broader Canadian
                workforce — all from one recruiter dashboard.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/sign-up"
                  className="inline-flex items-center rounded-full bg-primary px-6 py-3 font-semibold text-white shadow-lg shadow-primary/30 hover:opacity-90"
                >
                  Post your first job
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
                <a
                  href="#boards"
                  className="inline-flex items-center rounded-full border border-gray-300 bg-white px-6 py-3 font-semibold text-gray-800 hover:border-primary hover:text-primary"
                >
                  Explore the boards
                </a>
              </div>
              <p className="mt-6 text-sm text-gray-500">Already posting with us? <Link to="/sign-in" className="font-semibold text-primary hover:underline">Sign in</Link></p>
              <JobSearchCard />
            </div>
            <ExamplePosting />
          </div>

          {/* Facts strip */}
          <div className="relative border-y border-gray-100 bg-white/70">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                ['5 boards', 'plus national job search'],
                ['Since 2021', 'connecting employers and job seekers'],
                ['Salary', 'shown on every posting'],
                ['Canada-wide', 'jobs from coast to coast'],
              ].map(([big, small]) => (
                <div key={small}>
                  <p className="text-2xl font-extrabold font-[Poppins] text-primary">{big}</p>
                  <p className="text-sm text-gray-600">{small}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20">
          <SectionHeading
            eyebrow="What you get"
            title="Built for simpler recruitment"
            text="Publish once, distribute across specialized employment platforms, collect applications in one place, receive application notifications by email, and maintain a detailed history of your recruitment activity."
          />
          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <div key={title}>
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-red-50">
                  <Icon className="h-6 w-6 text-primary" />
                </span>
                <p className="mt-4 font-bold text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-600">{text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Mission */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-20 grid gap-10 lg:grid-cols-2 lg:items-center">
          <SectionHeading
            eyebrow="Why we exist"
            title="Good jobs should reach everyone."
            text="Many capable people are missed by mainstream job sites. CanadaJobLeads gives each of these communities its own board, and gives employers a simple way to reach them."
          />
          <ul className="space-y-4">
            {[
              'Every board is focused on one community, so job seekers find roles posted with them in mind.',
              'Postings are transparent: employer, location, salary and exact dates are always shown.',
              'Employers stay in control: every application arrives in their dashboard and their inbox.',
            ].map((line) => (
              <li key={line} className="flex items-start rounded-xl bg-gray-50 p-4">
                <CheckCircleIcon className="mr-3 mt-0.5 h-6 w-6 flex-shrink-0 text-primary" />
                <span className="text-gray-700">{line}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Boards */}
        <section id="boards" className="bg-gray-50 py-20 scroll-mt-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <SectionHeading eyebrow="Our boards" title="Five communities. One place to post." text="Pick one board or all five for each job." />
            <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {BOARDS.map((board) => (
                <a
                  key={board.name}
                  href={`https://${board.host}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 transition hover:-translate-y-1 hover:shadow-xl"
                >
                  <span className="absolute inset-x-0 top-0 h-1" style={{ backgroundColor: board.color }} aria-hidden="true" />
                  <span
                    className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-lg font-extrabold font-[Poppins]"
                    style={{ color: board.color, backgroundColor: `${board.color}1a` }}
                    aria-hidden="true"
                  >
                    {board.icon ? (
                      <img src={board.icon} alt="" className="h-6 w-6" />
                    ) : (
                      <SearchCircleIcon className="h-7 w-7" style={{ color: board.color }} />
                    )}
                  </span>
                  <p className="mt-4 text-lg font-bold text-gray-900">{board.name}</p>
                  <p className="mt-1 text-sm text-gray-600">{board.audience}</p>
                  <p className="mt-4 inline-flex items-center text-sm font-semibold" style={{ color: board.color }}>
                    {board.host.replace(/^www\./, '')}
                    <ArrowRightIcon className="ml-1 h-4 w-4 transition group-hover:translate-x-1" />
                  </p>
                </a>
              ))}
              <div className="flex flex-col justify-center rounded-2xl bg-gradient-to-br from-primary to-red-900 p-6 text-white">
                <p className="text-lg font-bold">Reach all five at once</p>
                <p className="mt-1 text-sm text-red-100">Save 10% when you post a job to two or more boards. Every posting also appears in our national job search.</p>
                <Link to="/sign-up" className="mt-4 inline-flex w-max items-center rounded-full bg-white px-4 py-2 text-sm font-semibold text-primary hover:bg-red-50">
                  Get started <ArrowRightIcon className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section id="how" className="max-w-6xl mx-auto px-4 sm:px-6 py-20 scroll-mt-16">
          <SectionHeading eyebrow="How it works" title="Live in minutes." />
          <ol className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map(({ icon: Icon, title, text }, i) => (
              <li key={title} className="relative rounded-2xl border border-gray-200 p-6">
                <span className="absolute -top-4 left-6 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-sm font-bold text-white shadow">
                  {i + 1}
                </span>
                <Icon className="h-8 w-8 text-primary" />
                <p className="mt-4 font-bold text-gray-900">{title}</p>
                <p className="mt-1 text-sm text-gray-600">{text}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Pricing */}
        <section id="pricing" className="bg-gray-50 py-20 scroll-mt-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <SectionHeading eyebrow="Pricing" title="Simple, per-board pricing." text="Pay per posting. No subscription, no contract." />
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {PRICES.map(({ days, price, label, badge }) => (
                <div
                  key={days}
                  className={`relative rounded-2xl bg-white p-8 ${badge ? 'border-2 border-primary shadow-xl' : 'border border-gray-200'}`}
                >
                  {badge && (
                    <span className="absolute -top-3 right-6 rounded-full bg-primary px-3 py-1 text-xs font-bold text-white">{badge}</span>
                  )}
                  <p className="text-sm font-semibold text-gray-500">{label}</p>
                  <p className="mt-3">
                    <span className="text-4xl font-extrabold font-[Poppins] text-gray-900">${price}</span>
                    <span className="ml-1 text-gray-500">CAD / board</span>
                  </p>
                  <p className="mt-2 text-sm text-gray-600">Live for {days} days</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-gray-600">
              <span className="font-semibold text-primary">Save 10%</span> when you post to two or more boards. Any
              account balance is applied automatically at checkout.
            </p>

            {/* High-volume / agency offer (REV-005, handled by email; the owner sets each offer). */}
            <div className="relative mt-10 overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 p-8 text-white shadow-xl">
              <MapleLeaf className="pointer-events-none absolute -right-8 -bottom-10 h-56 w-56 text-white/5" />
              <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div className="flex gap-4">
                  <span className="inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary">
                    <UserGroupIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="text-xs font-bold uppercase tracking-widest text-red-300">Agencies &amp; high-volume hiring</p>
                    <p className="mt-1 text-xl font-bold font-[Poppins]">Filling several roles, or posting for clients?</p>
                    <p className="mt-2 max-w-xl text-sm text-gray-300">
                      Ask us about prepaid account credit at promotional rates. Tell us roughly how many postings you have
                      in mind and we'll put together an offer. Your credit is applied automatically at checkout.
                    </p>
                  </div>
                </div>
                <CopyEmail
                  className="text-base text-white md:flex-shrink-0"
                  buttonClassName="rounded-full border border-white/40 px-3 py-1 text-sm font-semibold text-white hover:bg-white/10"
                />
              </div>
            </div>
          </div>
        </section>


        {/* FAQ */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
          <SectionHeading eyebrow="Questions" title="Good to know" />
          <dl className="mt-8 grid gap-6 md:grid-cols-2">
            {FAQS.map(({ q, a }) => (
              <div key={q} className="rounded-2xl border border-gray-200 p-6">
                <dt className="font-semibold text-gray-900">{q}</dt>
                <dd className="mt-2 text-sm text-gray-600">{a}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-8 flex flex-col items-start gap-3 rounded-2xl border border-red-100 bg-red-50 p-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-bold text-gray-900">Still have questions?</p>
              <p className="text-sm text-gray-600">Email us and we'll get back to you.</p>
            </div>
            <CopyEmail
              className="text-lg text-primary"
              buttonClassName="rounded-full border border-primary px-3 py-1 text-sm font-semibold text-primary hover:bg-white"
            />
          </div>
        </section>

        {/* Final call to action */}
        <section className="relative overflow-hidden bg-gradient-to-br from-primary to-red-900">
          <MapleLeaf className="pointer-events-none absolute -right-10 -top-10 h-72 w-72 text-white/10" />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="text-3xl font-extrabold font-[Poppins] text-white">Ready to reach more candidates?</h2>
              <p className="mt-2 text-red-100">Create your employer account and post your first job today.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/sign-up" className="rounded-full bg-white px-6 py-3 font-semibold text-primary shadow-lg hover:bg-red-50">
                Post a job
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-gray-900 text-gray-400">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid gap-8 md:grid-cols-3">
          <div>
            <div className="flex items-center">
              <SearchCircleIcon className="text-primary mr-2 w-8 h-8" aria-hidden="true" />
              <span className="font-extrabold font-[Poppins] text-lg text-white">Canada Job Leads</span>
            </div>
            <p className="mt-3 text-sm">Canadian job boards for communities that are often overlooked.</p>
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Our boards</p>
            <ul className="mt-3 space-y-1 text-sm">
              {BOARDS.map((b) => (
                <li key={b.name}>
                  <a href={`https://${b.host}`} target="_blank" rel="noopener noreferrer" className="hover:text-white">
                    {b.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Company</p>
            <ul className="mt-3 space-y-1 text-sm">
              <li><Link to="/jobs" className="hover:text-white">Search jobs</Link></li>
              <li><Link to="/sign-in" className="hover:text-white">Employer sign in</Link></li>
              <li><Link to="/terms" className="hover:text-white">Terms &amp; Conditions</Link></li>
              <li>
                <CopyEmail
                  className="text-gray-200"
                  buttonClassName="rounded border border-gray-600 px-2 py-0.5 text-xs text-gray-300 hover:border-gray-400 hover:text-white"
                />
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800">
          <p className="max-w-6xl mx-auto px-4 sm:px-6 py-4 text-xs">© {new Date().getFullYear()} CanadaJobLeads, a Vinara Software Co. product</p>
        </div>
      </footer>
    </div>
  );
}

export default Home;
